'use client';
/**
 * 검색 v2 제안 구역 — 헤더 ⌘K 모달·홈 히어로가 «같은» 훅(useSearchSuggest)을 쓴다.
 * 「지역 안에서」 토글(현재 URL 지역이 있을 때만), 무결과 → 서버가 search_misses 기록 + 상담 CTA.
 * ⛔ 이모지·그라데이션 없음. 서버 HTML 에 나오지 않는다(모달·드롭다운 안에서만).
 */
import { useEffect } from 'react';
import { useSearchSuggest, type SuggestItem } from '@/lib/search/use-search-suggest';
import { track } from '@/lib/analytics';
import { lifecycleLabel } from '@/lib/apt/lifecycle-label';

export interface RegionScope { code: string; label: string }

export default function SuggestSection({
  q, source, scope, scoped, onToggleScope, onPick, onEmptyChange, activeIdx = -1,
}: {
  q: string;
  source: string;
  scope: RegionScope | null;
  scoped: boolean;
  onToggleScope: (v: boolean) => void;
  onPick: (item: SuggestItem) => void;
  onEmptyChange?: (empty: boolean) => void;
  activeIdx?: number;
}) {
  const s = useSearchSuggest(q, { source, regionCode: scope && scoped ? scope.code : null, limit: 6 });
  useEffect(() => { onEmptyChange?.(s.empty); }, [s.empty, onEmptyChange]);
  if (!q.trim()) return null;
  return (
    <div className="mb-3" data-search-v2="suggest">
      <div className="mb-1 flex items-center justify-between px-2">
        <h3 className="text-[length:var(--fs-xs)]" style={{ fontWeight: 'var(--fw-title)', color: 'var(--ink-2)' }}>현장 바로가기</h3>
        {scope ? (
          <label className="flex items-center gap-1 text-xs" style={{ minHeight: 'var(--touch-min, 44px)', color: 'var(--ink-2)' }}>
            <input type="checkbox" checked={scoped} onChange={(e) => onToggleScope(e.target.checked)} />
            {scope.label} 안에서
          </label>
        ) : null}
      </div>
      {s.items.length ? (
        <ul role="listbox" aria-label="현장 제안">
          {s.items.map((it, i) => (
            <li key={it.slug} role="option" aria-selected={i === activeIdx}>
              <a
                href={`/apt/${encodeURIComponent(it.slug)}`}
                onClick={(e) => { e.preventDefault(); s.select(it.slug, i + 1); onPick(it); }}
                className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left"
                style={{ minHeight: 'var(--touch-min, 44px)', background: i === activeIdx ? 'var(--chip-live-bg)' : undefined }}
              >
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-1 text-sm" style={{ fontWeight: 'var(--fw-title)', color: 'var(--ink)' }}>{it.name}</span>
                  <span className="line-clamp-1 text-xs" style={{ color: 'var(--ink-2)' }}>{[it.region, it.sigungu, lifecycleLabel(it.stage)].filter(Boolean).join(' · ')}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : s.empty ? (
        <p className="px-2 py-1 text-xs" style={{ color: 'var(--ink-2)' }}>
          {scope && scoped ? (
            <>
              {scope.label} 안에서는 맞는 현장이 없어요.{' '}
              <button type="button" className="underline" onClick={() => onToggleScope(false)}>전국에서 찾기</button>
            </>
          ) : '맞는 현장이 없어요.'}
        </p>
      ) : null}
    </div>
  );
}

/** 무결과 상담 CTA — 헤더·히어로·/search 공용. */
export function SearchMissCta({ q, source }: { q: string; source: string }) {
  return (
    <div className="rounded-lg p-3 text-left" style={{ border: '1px solid var(--line)', background: 'var(--card)' }} data-search-v2="miss-cta">
      <p className="text-sm" style={{ fontWeight: 'var(--fw-title)', color: 'var(--ink)' }}>찾는 현장이 없나요?</p>
      <p className="mt-1 text-xs" style={{ color: 'var(--ink-2)' }}>아직 공고 전이거나 이름이 다를 수 있어요. 상담으로 물어보면 확인해 드려요.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <a href={`/consultant?utm_source=search&utm_medium=miss&utm_campaign=${encodeURIComponent(source)}`} data-search-miss-cta="consult"
          onClick={() => track('search_miss_cta', 'consult', { q, source })}
          className="inline-flex items-center rounded-lg px-3 text-xs" style={{ minHeight: 'var(--touch-min, 44px)', background: 'var(--navy)', color: 'var(--on-navy)', fontWeight: 'var(--fw-title)' }}>
          상담으로 물어보기
        </a>
        <a href="/board/write?cat=qna" data-search-miss-cta="board"
          onClick={() => track('search_miss_cta', 'board', { q, source })}
          className="inline-flex items-center rounded-lg px-3 text-xs" style={{ minHeight: 'var(--touch-min, 44px)', border: '1px solid var(--line-2)', color: 'var(--ink)', fontWeight: 'var(--fw-title)' }}>
          질문 게시판에 남기기
        </a>
      </div>
    </div>
  );
}
